'use client';

import { ArrowRight } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { generateUUID } from '@/lib/utils';
import type { ExpertModePrompt } from '@/lib/utils/expert-mode-parser';

interface ExpertModeButtonProps {
  prompt: ExpertModePrompt;
  chatId: string;
  userId?: string | null;
  companyName?: string | null;
  userName?: string | null;
  basePath?: string;
}

export function ExpertModeButton({
  prompt,
  chatId,
  userId,
  companyName,
  userName,
  basePath = '/chat',
}: ExpertModeButtonProps) {
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);

  const handleExpertMode = () => {
    setIsNavigating(true);
    
    // Generate a new conversation ID for expert mode (UUID format for database)
    const newChatId = generateUUID();
    
    // Build query parameters
    const params = new URLSearchParams();
    params.set('context', prompt.context);
    if (userId) params.set('userId', userId);
    if (companyName) params.set('companyName', companyName);
    if (userName) params.set('userName', userName);

    // Determine expert mode base path from current basePath
    const expertBasePath = basePath.includes('/iframe') ? '/iframe/expert' : '/expert';

    // Navigate to expert mode with context (new conversation)
    router.push(`${expertBasePath}/${newChatId}?${params.toString()}`);
  };

  return (
    <div className="mt-6 rounded-xl border-2 border-blue-200 bg-blue-50 p-6">
      <div className="mb-4">
        <h3 className="font-semibold" style={{ color: '#000054' }}>{prompt.title}</h3>
        <p className="mt-2 text-sm" style={{ color: '#000054' }}>{prompt.subtitle}</p>
      </div>
      <button
        type="button"
        onClick={handleExpertMode}
        disabled={isNavigating}
        className="flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
        style={{ backgroundColor: '#000054' }}
      >
        {isNavigating ? (
          <>
            <div className="size-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Chargement...
          </>
        ) : (
          <>
            Passer en mode expert
            <ArrowRight className="size-5" />
          </>
        )}
      </button>
    </div>
  );
}
